'use client';

/**
 * Service Delivery Progress Component
 *
 * Displays and manages service delivery progress for a referral:
 * - Progress notes/session records
 * - Units delivered tracking
 * - Progress timeline visualization
 *
 * Used in case workspace for tracking service delivery.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Plus,
  Calendar,
  Clock,
  FileText,
  User,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  serviceReferralsAPI,
  ServiceProgress,
  ServiceProgressCreate,
  SessionType,
} from '@/services/api/serviceReferrals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ServiceDeliveryProgressProps {
  referralId: number;
  estimatedUnits?: number;
  actualUnits?: number;
  onProgressAdded?: (progress: ServiceProgress) => void;
  readOnly?: boolean;
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const getSessionTypes = (t: (key: string, fallback: string) => string): { value: SessionType; label: string; color: string }[] => [
  { value: 'intake', label: t('serviceReferrals.delivery.sessionType.intake', 'Intake'), color: 'blue' },
  { value: 'assessment', label: t('serviceReferrals.delivery.sessionType.assessment', 'Assessment'), color: 'purple' },
  { value: 'regular', label: t('serviceReferrals.delivery.sessionType.regular', 'Regular Session'), color: 'green' },
  { value: 'follow_up', label: t('serviceReferrals.delivery.sessionType.followUp', 'Follow-up'), color: 'teal' },
  { value: 'crisis', label: t('serviceReferrals.delivery.sessionType.crisis', 'Crisis'), color: 'red' },
  { value: 'discharge', label: t('serviceReferrals.delivery.sessionType.discharge', 'Discharge'), color: 'gray' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function ServiceDeliveryProgress({
  referralId,
  estimatedUnits = 0,
  actualUnits = 0,
  onProgressAdded,
  readOnly = false,
  className = '',
}: ServiceDeliveryProgressProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const SESSION_TYPES = getSessionTypes(t);

  // State
  const [progressRecords, setProgressRecords] = useState<ServiceProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedRecordId, setExpandedRecordId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form State
  const [formData, setFormData] = useState<ServiceProgressCreate>({
    session_date: new Date().toISOString().split('T')[0],
    session_type: 'regular',
    duration_minutes: 60,
    units_delivered: 1,
    progress_notes: '',
    goals_addressed: [],
    interventions_used: [],
    client_response: '',
    next_steps: '',
    staff_name: '',
    staff_credential: '',
  });

  // Load progress records
  useEffect(() => {
    loadProgressRecords();
  }, [referralId]);

  const loadProgressRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await serviceReferralsAPI.getProgress(referralId);
      setProgressRecords(data);
    } catch (err) {
      console.error('Failed to load progress:', err);
      setError(t('serviceReferrals.delivery.errors.loadFailed', 'Failed to load progress records'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.progress_notes.trim()) {
      setError(t('serviceReferrals.delivery.errors.notesRequired', 'Progress notes are required'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const newProgress = await serviceReferralsAPI.addProgress(referralId, formData);
      setProgressRecords([newProgress, ...progressRecords]);
      setShowAddForm(false);
      resetForm();
      onProgressAdded?.(newProgress);
    } catch (err) {
      console.error('Failed to save progress:', err);
      setError(t('serviceReferrals.delivery.errors.saveFailed', 'Failed to save progress record'));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      session_date: new Date().toISOString().split('T')[0],
      session_type: 'regular',
      duration_minutes: 60,
      units_delivered: 1,
      progress_notes: '',
      goals_addressed: [],
      interventions_used: [],
      client_response: '',
      next_steps: '',
      staff_name: '',
      staff_credential: '',
    });
  };

  const getSessionTypeInfo = (type: SessionType) => {
    return SESSION_TYPES.find((t) => t.value === type) || SESSION_TYPES[2];
  };

  const progressPercentage = estimatedUnits > 0 ? Math.min(100, (actualUnits / estimatedUnits) * 100) : 0;

  const formatDate = (dateStr: string) => {
    return fmt.medium(dateStr);
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{t('serviceReferrals.delivery.title', 'Service Delivery Progress')}</CardTitle>
            <CardDescription>{t('serviceReferrals.delivery.description', 'Track sessions and progress notes')}</CardDescription>
          </div>
          {!readOnly && (
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              variant={showAddForm ? 'outline' : 'default'}
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              {showAddForm ? t('serviceReferrals.common.cancel', 'Cancel') : t('serviceReferrals.delivery.addSession', 'Add Session')}
            </Button>
          )}
        </div>

        {/* Progress Bar */}
        {estimatedUnits > 0 && (
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('serviceReferrals.delivery.unitsProgress', 'Units Progress')}</span>
              <span className="font-medium">
                {actualUnits} / {estimatedUnits} units ({Math.round(progressPercentage)}%)
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
            {progressPercentage >= 100 && (
              <div className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                {t('serviceReferrals.delivery.unitsComplete', 'Service units complete')}
              </div>
            )}
            {progressPercentage >= 75 && progressPercentage < 100 && (
              <div className="flex items-center gap-1 text-sm text-amber-600">
                <AlertTriangle className="w-4 h-4" />
                {t('serviceReferrals.delivery.approachingLimit', 'Approaching unit limit')}
              </div>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent>
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Add Session Form */}
        {showAddForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg bg-gray-50 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Session Date */}
              <div>
                <Label htmlFor="session_date">{t('serviceReferrals.delivery.sessionDate', 'Session Date')}</Label>
                <Input
                  id="session_date"
                  type="date"
                  value={formData.session_date}
                  onChange={(e) => setFormData({ ...formData, session_date: e.target.value })}
                  required
                />
              </div>

              {/* Session Type */}
              <div>
                <Label htmlFor="session_type">{t('serviceReferrals.delivery.sessionType', 'Session Type')}</Label>
                <select
                  id="session_type"
                  value={formData.session_type}
                  onChange={(e) => setFormData({ ...formData, session_type: e.target.value as SessionType })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {SESSION_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Duration */}
              <div>
                <Label htmlFor="duration">{t('serviceReferrals.delivery.duration', 'Duration (minutes)')}</Label>
                <Input
                  id="duration"
                  type="number"
                  min={1}
                  value={formData.duration_minutes || ''}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
                />
              </div>

              {/* Units Delivered */}
              <div>
                <Label htmlFor="units">{t('serviceReferrals.delivery.unitsDelivered', 'Units Delivered')}</Label>
                <Input
                  id="units"
                  type="number"
                  min={1}
                  value={formData.units_delivered}
                  onChange={(e) => setFormData({ ...formData, units_delivered: parseInt(e.target.value) || 1 })}
                  required
                />
              </div>

              {/* Staff Name */}
              <div>
                <Label htmlFor="staff_name">{t('serviceReferrals.delivery.staffName', 'Staff Name')}</Label>
                <Input
                  id="staff_name"
                  value={formData.staff_name || ''}
                  onChange={(e) => setFormData({ ...formData, staff_name: e.target.value })}
                  placeholder={t('serviceReferrals.delivery.staffNamePlaceholder', 'e.g., Jane Smith')}
                />
              </div>

              {/* Staff Credential */}
              <div>
                <Label htmlFor="staff_credential">{t('serviceReferrals.delivery.credential', 'Credential')}</Label>
                <Input
                  id="staff_credential"
                  value={formData.staff_credential || ''}
                  onChange={(e) => setFormData({ ...formData, staff_credential: e.target.value })}
                  placeholder={t('serviceReferrals.delivery.credentialPlaceholder', 'e.g., LCSW, CHW')}
                />
              </div>
            </div>

            {/* Progress Notes */}
            <div>
              <Label htmlFor="progress_notes">{t('serviceReferrals.delivery.progressNotesLabel', 'Progress Notes *')}</Label>
              <Textarea
                id="progress_notes"
                value={formData.progress_notes}
                onChange={(e) => setFormData({ ...formData, progress_notes: e.target.value })}
                placeholder={t('serviceReferrals.delivery.progressNotesPlaceholder', 'Describe the session, interventions used, and client progress...')}
                rows={4}
                required
              />
            </div>

            {/* Client Response */}
            <div>
              <Label htmlFor="client_response">{t('serviceReferrals.delivery.clientResponse', 'Client Response')}</Label>
              <Textarea
                id="client_response"
                value={formData.client_response || ''}
                onChange={(e) => setFormData({ ...formData, client_response: e.target.value })}
                placeholder={t('serviceReferrals.delivery.clientResponsePlaceholder', 'How did the client respond to interventions?')}
                rows={2}
              />
            </div>

            {/* Next Steps */}
            <div>
              <Label htmlFor="next_steps">{t('serviceReferrals.delivery.nextSteps', 'Next Steps')}</Label>
              <Textarea
                id="next_steps"
                value={formData.next_steps || ''}
                onChange={(e) => setFormData({ ...formData, next_steps: e.target.value })}
                placeholder={t('serviceReferrals.delivery.nextStepsPlaceholder', 'Plan for next session or follow-up actions...')}
                rows={2}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                {t('serviceReferrals.common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('serviceReferrals.delivery.saveSession', 'Save Session')}
              </Button>
            </div>
          </form>
        )}

        {/* Loading State */}
        {loading && (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}

        {/* Progress Records List */}
        {!loading && progressRecords.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>{t('serviceReferrals.delivery.noRecords', 'No progress records yet')}</p>
            {!readOnly && (
              <Button variant="link" onClick={() => setShowAddForm(true)}>
                {t('serviceReferrals.delivery.addFirstSession', 'Add the first session')}
              </Button>
            )}
          </div>
        )}

        {!loading && progressRecords.length > 0 && (
          <div className="space-y-3">
            {progressRecords.map((record) => {
              const typeInfo = getSessionTypeInfo(record.session_type);
              const isExpanded = expandedRecordId === record.id;

              return (
                <div
                  key={record.id}
                  className="border rounded-lg overflow-hidden"
                >
                  {/* Header Row */}
                  <div
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedRecordId(isExpanded ? null : record.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Badge
                        variant="outline"
                        className={`
                          ${typeInfo.color === 'blue' ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}
                          ${typeInfo.color === 'purple' ? 'border-purple-300 text-purple-700 bg-purple-50' : ''}
                          ${typeInfo.color === 'green' ? 'border-green-300 text-green-700 bg-green-50' : ''}
                          ${typeInfo.color === 'teal' ? 'border-teal-300 text-teal-700 bg-teal-50' : ''}
                          ${typeInfo.color === 'red' ? 'border-red-300 text-red-700 bg-red-50' : ''}
                          ${typeInfo.color === 'gray' ? 'border-gray-300 text-gray-700 bg-gray-50' : ''}
                        `}
                      >
                        {typeInfo.label}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        {formatDate(record.session_date)}
                      </div>
                      {record.duration_minutes && (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          {record.duration_minutes} min
                        </div>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {record.units_delivered} unit{record.units_delivered !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 py-3 border-t bg-gray-50 space-y-3">
                      {record.staff_name && (
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{record.staff_name}</span>
                          {record.staff_credential && (
                            <Badge variant="outline" className="text-xs">
                              {record.staff_credential}
                            </Badge>
                          )}
                        </div>
                      )}

                      <div>
                        <Label className="text-xs text-gray-500">{t('serviceReferrals.delivery.progressNotes', 'Progress Notes')}</Label>
                        <p className="text-sm whitespace-pre-wrap">{record.progress_notes}</p>
                      </div>

                      {record.client_response && (
                        <div>
                          <Label className="text-xs text-gray-500">{t('serviceReferrals.delivery.clientResponse', 'Client Response')}</Label>
                          <p className="text-sm">{record.client_response}</p>
                        </div>
                      )}

                      {record.next_steps && (
                        <div>
                          <Label className="text-xs text-gray-500">{t('serviceReferrals.delivery.nextSteps', 'Next Steps')}</Label>
                          <p className="text-sm">{record.next_steps}</p>
                        </div>
                      )}

                      {record.goals_addressed && record.goals_addressed.length > 0 && (
                        <div>
                          <Label className="text-xs text-gray-500">{t('serviceReferrals.delivery.goalsAddressed', 'Goals Addressed')}</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {record.goals_addressed.map((goal, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {goal}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {record.interventions_used && record.interventions_used.length > 0 && (
                        <div>
                          <Label className="text-xs text-gray-500">{t('serviceReferrals.delivery.interventionsUsed', 'Interventions Used')}</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {record.interventions_used.map((intervention, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {intervention}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="text-xs text-gray-400 pt-2 border-t">
                        {t('serviceReferrals.delivery.recordedOn', 'Recorded on')} {fmt.dateTime(record.created_at)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ServiceDeliveryProgress;
